import { Controller } from "@hotwired/stimulus"

// Manages the two-step "Add to Pantry" form flow:
//   Step 1: Search bar only (initial state)
//   Step 2a: Product selected -> show details form (read-only product info + editable fields)
//   Step 2b: Create new product -> show full creation form
export default class extends Controller {
  static targets = [
    "input", "results", "productId", "selectedDisplay",
    "storageLocation", "createSection", "detailsSection",
    "categoryDisplay", "createLink",
    "newProductName", "newStorageLocation", "newQuantity", "newNotes"
  ]
  static values = { url: String }

  connect() {
    this.debounceTimer = null
  }

  disconnect() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  // --- Search ---

  search() {
    const query = this.inputTarget.value.trim()

    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    if (query.length < 2) {
      this.resultsTarget.style.display = "none"
      this.resultsTarget.innerHTML = ""
      return
    }

    this.debounceTimer = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    try {
      const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
      const response = await fetch(url, {
        headers: { "Accept": "text/html" }
      })

      if (response.ok) {
        const html = await response.text()
        this.resultsTarget.innerHTML = html
        this.resultsTarget.style.display = html.trim().length > 0 ? "block" : "none"
      }
    } catch (error) {
      console.error("Product search error:", error)
    }
  }

  // --- Step 2a: Select existing product ---

  selectProduct(event) {
    const button = event.currentTarget
    const productId = button.dataset.productId
    const productName = button.dataset.productName
    const categoryName = button.dataset.categoryName
    const storageLocationId = button.dataset.storageLocationId

    // Set hidden product_id
    this.productIdTarget.value = productId

    // Show selected product badge
    this.selectedDisplayTarget.innerHTML = `
      <span class="badge bg-success fs-6 d-inline-flex align-items-center gap-1">
        ${this.escapeHtml(productName)}
        <button type="button" class="btn-close btn-close-white ms-1" style="font-size: 0.6rem;"
                data-action="product-search#clearSelection"></button>
      </span>
    `
    this.selectedDisplayTarget.style.display = "block"

    // Hide search input and results
    this.inputTarget.value = ""
    this.inputTarget.style.display = "none"
    this.resultsTarget.style.display = "none"
    this.resultsTarget.innerHTML = ""

    // Hide "Create one" link
    if (this.hasCreateLinkTarget) {
      this.createLinkTarget.style.display = "none"
    }

    // Set category display
    if (this.hasCategoryDisplayTarget) {
      this.categoryDisplayTarget.textContent = categoryName || ""
    }

    // Auto-select storage location
    if (storageLocationId && this.hasStorageLocationTarget) {
      this.storageLocationTarget.value = storageLocationId
    }

    // Show details section (Step 2a), hide create section
    if (this.hasDetailsSectionTarget) {
      this.detailsSectionTarget.style.display = "block"
    }
    if (this.hasCreateSectionTarget) {
      this.createSectionTarget.style.display = "none"
    }
  }

  // --- Clear selected product (back to search within Step 2a) ---

  clearSelection() {
    this.productIdTarget.value = ""
    this.selectedDisplayTarget.style.display = "none"
    this.selectedDisplayTarget.innerHTML = ""
    this.inputTarget.style.display = "block"
    this.inputTarget.focus()

    // Show "Create one" link again
    if (this.hasCreateLinkTarget) {
      this.createLinkTarget.style.display = "block"
    }

    // Hide details section since no product selected
    if (this.hasDetailsSectionTarget) {
      this.detailsSectionTarget.style.display = "none"
    }
  }

  // --- Step 2b: Show create new product form ---

  showCreateForm(event) {
    event.preventDefault()

    // Pre-fill the product name from the search input
    const searchQuery = this.inputTarget.value.trim()
    if (searchQuery && this.hasNewProductNameTarget) {
      this.newProductNameTarget.value = searchQuery
    }

    // Hide search results and details section
    this.resultsTarget.style.display = "none"
    this.resultsTarget.innerHTML = ""

    if (this.hasDetailsSectionTarget) {
      this.detailsSectionTarget.style.display = "none"
    }

    // Hide "Create one" link (we are already in create mode)
    if (this.hasCreateLinkTarget) {
      this.createLinkTarget.style.display = "none"
    }

    // Hide search input and selected display
    this.inputTarget.style.display = "none"
    this.selectedDisplayTarget.style.display = "none"

    // Clear any previously selected product
    this.productIdTarget.value = ""

    // Show create section
    if (this.hasCreateSectionTarget) {
      this.createSectionTarget.style.display = "block"
    }
  }

  // --- Cancel / Reset back to Step 1 ---

  resetToSearch() {
    // Clear product selection
    this.productIdTarget.value = ""
    this.selectedDisplayTarget.style.display = "none"
    this.selectedDisplayTarget.innerHTML = ""

    // Show search input, clear it
    this.inputTarget.value = ""
    this.inputTarget.style.display = "block"

    // Hide results
    this.resultsTarget.style.display = "none"
    this.resultsTarget.innerHTML = ""

    // Show "Create one" link
    if (this.hasCreateLinkTarget) {
      this.createLinkTarget.style.display = "block"
    }

    // Hide both step 2 sections
    if (this.hasDetailsSectionTarget) {
      this.detailsSectionTarget.style.display = "none"
    }
    if (this.hasCreateSectionTarget) {
      this.createSectionTarget.style.display = "none"
    }

    // Clear create form fields
    if (this.hasNewProductNameTarget) this.newProductNameTarget.value = ""
    if (this.hasNewStorageLocationTarget) this.newStorageLocationTarget.value = ""
    if (this.hasNewQuantityTarget) this.newQuantityTarget.value = "1"
    if (this.hasNewNotesTarget) this.newNotesTarget.value = ""

    // Focus the search input
    this.inputTarget.focus()
  }

  // --- Utility ---

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
